import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import cn from "classnames";
import styles from "./Settings.module.sass";
import CompanyInformation from "./CompanyInformation";
import AddressInformation from "./AddressInformation";
import Confirmation from "./Confirmation";
import RepresentativeInformation from "./RepresentativeInformation";
import { getFolderById, updateFolder } from "../../services/folder.service";
import { updateCompany } from "../../services/company.service";
import { toast } from "react-toastify";


const CompanyCheckInformation = () => {
  const { id: folderId } = useParams(); // Récupérer l'ID du folder depuis l'URL

  // États pour la navigation et les données
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [companyData, setCompanyData] = useState({});
  const [folderData, setFolderData] = useState({
    companyInfo: {},
    addressInfo: {},
    representativeInfo: {}
  });

  // Initialiser les données du formulaire avec les données existantes de l'entreprise
  useEffect(() => {
    if (companyData && Object.keys(companyData).length > 0) {
      setFolderData(prev => ({
        ...prev,
        companyInfo: {
          name: companyData.name || '',
          siret: companyData.siret || '',
          employee_count: companyData.employee_count || '',
          activity_sector: companyData.activity_sector || '',
          description: companyData.description || ''
        },
        addressInfo: {
          address: companyData.address || '',
          address_complement: companyData.address_complement || '',
          zip_code: companyData.zip_code || '',
          city: companyData.city || '',
          country: companyData.country || 'France',
          department: companyData.department || ''
        },
        representativeInfo: {
          last_name: companyData.director_last_name || '',
          first_name: companyData.director_first_name || '',
          email: companyData.director_email || '',
          phone: companyData.director_phone || ''
        }
      }));
    }
  }, [companyData]);

  // Configuration des étapes pour l'entreprise
  const steps = [
    {
      id: 0,
      title: "Informations de l'entreprise",
      component: CompanyInformation,
    },
    {
      id: 1,
      title: "Adresse de l'entreprise",
      component: AddressInformation,
    },
    {
      id: 2,
      title: "Représentant légal",
      component: RepresentativeInformation,
    },
    {
      id: 3,
      title: "Confirmation",
      component: Confirmation,
    }
  ];


  // Récupérer les informations du dossier et de l'entreprise au chargement du composant
  useEffect(() => {
    const fetchFolderData = async () => {
      if (!folderId) {
        setError("ID du dossier manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const folder = await getFolderById(folderId);
        
        if (folder.expand?.company_id) {
          setCompanyData(folder.expand.company_id);
        }
        
        setError(null);
      } catch (error) {
        console.error('Erreur lors de la récupération des données du dossier:', error);
        setError("Impossible de récupérer les informations du dossier");
      } finally {
        setLoading(false);
      }
    };

    fetchFolderData();
  }, [folderId]);


  // Navigation entre les étapes
  const goToNextStep = async () => {
    if (currentStep < steps.length - 1) {
      // Si on passe de l'étape 0 (CompanyInformation) à l'étape suivante
      if (currentStep === 0 && folderData.companyInfo && Object.keys(folderData.companyInfo).length > 0) {
        try {
          // Mettre à jour les informations de l'entreprise
          await updateCompany(companyData.id, folderData.companyInfo);
          console.log('Informations entreprise mises à jour');
        } catch (error) {
          console.error('Erreur lors de la mise à jour des informations entreprise:', error);
        }
      }

      // Si on passe de l'étape 1 (AddressInformation) à l'étape suivante
      if (currentStep === 1 && folderData.addressInfo && Object.keys(folderData.addressInfo).length > 0) {
        try {
          // Mettre à jour les informations d'adresse de l'entreprise
          const addressData = {
            address: folderData.addressInfo.address,
            address_complement: folderData.addressInfo.address_complement,
            zip_code: folderData.addressInfo.zip_code,
            city: folderData.addressInfo.city,
            country: folderData.addressInfo.country || 'France',
            department: folderData.addressInfo.department
          };
          
          await updateCompany(companyData.id, addressData);
          console.log('Informations d\'adresse mises à jour');
        } catch (error) {
          console.error('Erreur lors de la mise à jour des informations d\'adresse:', error);
        }
      }

      // Si on passe de l'étape 2 (RepresentativeInformation) à l'étape 3 (Confirmation)
      if (currentStep === 2) {
        try {
          // Mettre à jour les informations du représentant légal
          if (folderData.representativeInfo && Object.keys(folderData.representativeInfo).length > 0) {
            const representativeData = {
              director_last_name: folderData.representativeInfo.last_name,
              director_first_name: folderData.representativeInfo.first_name,
              director_email: folderData.representativeInfo.email,
              director_phone: folderData.representativeInfo.phone
            };
            
            await updateCompany(companyData.id, representativeData);
            console.log('Informations représentant mises à jour');
          }

          // Mettre à jour la progression : marquer les infos entreprise comme remplies
          try {
            const currentFolder = await getFolderById(folderId);
            const currentProgression = currentFolder.progression || {};

            // Mettre à jour uniquement infos_remplies pour l'entreprise sans écraser le reste
            const updatedProgression = {
              ...currentProgression,
              entreprise: {
                ...currentProgression.entreprise,
                infos_remplies: true
              }
            };

            await updateFolder(folderId, {
              progression: updatedProgression
            });

            console.log('Progression mise à jour : infos_remplies = true pour entreprise');
          } catch (progressionError) {
            console.error('Erreur lors de la mise à jour de la progression:', progressionError);
          }
        } catch (error) {
          console.error('Erreur lors de la sauvegarde des données entreprise:', error);
        }
      }

      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Mise à jour des données du formulaire
  const updateStepData = (stepKey, data) => {
    console.log('updateStepData appelé avec:', stepKey, data);
    setFolderData(prev => {
      const newData = {
        ...prev,
        [stepKey]: typeof data === 'object' && data !== null ? { ...prev[stepKey], ...data } : data
      };
      console.log('Nouvelles données folderData:', newData);
      return newData;
    });
  };

  // Rendu du composant de l'étape actuelle
  const CurrentStepComponent = steps[currentStep].component;

  // Affichage de chargement
  if (loading) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Chargement des informations du dossier...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            <p>Erreur : {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      {/* Navigation par étapes */}
      <div className={styles.menu}>
        {steps.map((step, index) => (
          <button
            className={cn(styles.button, {
              [styles.active]: currentStep === index,
              [styles.completed]: currentStep > index,
            })}
            key={step.id}
          >
            <span className={styles.stepNumber}>{index + 1}</span>
            {step.title}
          </button>
        ))}
      </div>

      {/* Contenu de l'étape actuelle */}
      <div className={styles.wrapper}>
        <div className={styles.stepContent}>
          <div className={styles.componentWrapper}>
            <CurrentStepComponent
              data={{ company: companyData, ...folderData }}
              updateData={updateStepData}
              currentStep={currentStep}
            />
          </div>
        </div>

        {/* Boutons de navigation */}
        {currentStep < steps.length - 1 && (
          <div className={styles.navigationButtons}>
            <button
              className="button-stroke"
              onClick={goToPreviousStep}
              disabled={currentStep === 0 || saving}
            >
              Précédent
            </button>

            <button
              className="button"
              onClick={goToNextStep}
              disabled={saving}
            >
              {saving ? "Sauvegarde..." : "Suivant"}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default CompanyCheckInformation;
